/**
 * Die Aufteilung des Verbrauchs auf seine Teile (HM-118, aus
 * `ui_kits/dashboard/loads.jsx`).
 *
 * **Teile teilen auf, sie addieren nicht.** Der Verbrauch einer Anlage ist das
 * Ganze; die Streifen eines Stapels sind Teile davon. Woraus sie bestehen,
 * hängt am Messkonzept, und es gibt drei Fälle:
 *
 * - **Haus** (`residual_consumption` an der Anlage, HM-110): Der Hauptzähler
 *   zählt alles, eine Wallbox mit eigenem Zähler ist ein Teil davon, und der
 *   **übrige Verbrauch** ist gerechnet — Verbrauch minus Verbraucher, bei null
 *   geklemmt. Er steht in der blassesten Stufe.
 * - **Mieterstrom** (`unit_consumption` an der Anlage): Der Verbrauch ist die
 *   Summe der Unterzähler (HM-080). Seine Teile sind die **Wohneinheiten
 *   zusammen** und die **weiteren Verbraucher** — alles gemessen, es gibt
 *   keinen Rest. Ein Verbraucher hinter dem Zähler einer Wohnung steckt in ihr
 *   (HM-111) und ist kein eigener Teil. Die Wohnungen einzeln stehen in ihrer
 *   Tabelle; zwölf Streifen einer Farbe wären keiner.
 * - **Wohnung** (`unitConsumptionSplit`, HM-111): wie das Haus eine Ebene
 *   tiefer — der Zähler der Wohnung zählt alles, die Verbraucher dahinter sind
 *   Teile davon, und der übrige Verbrauch der Wohnung ist gerechnet.
 *
 * Entschieden wird an den Größen der Anlage, nicht am Schlüssel der Vorlage.
 *
 * **Die Farben sind eine Helligkeitsreihe derselben Größe** mit vier Stufen.
 * Ein Teil mehr bekäme die Farbe eines anderen — zwei gleiche Streifen in einem
 * Stapel sind nicht auseinanderzuhalten. Dann wird nicht gestapelt, sondern nur
 * aufgezählt (Regel 9): Die Zahlen stimmen weiter, nur das Bild fehlt.
 */
import type { ApiComponents } from "@hausmeter/sdk";

type Energy = ApiComponents["schemas"]["SiteEnergyResponse"];
type EnergySeries = ApiComponents["schemas"]["SiteEnergySeries"];
type Total = ApiComponents["schemas"]["SiteEnergyTotal"];
type Origin = ApiComponents["schemas"]["EnergyOrigin"];
type UnitEnergy = ApiComponents["schemas"]["UnitEnergyResponse"];

/** Die Stufen der Größe Verbrauch, vom kräftigsten zum blassesten. */
export const CONSUMPTION_SHADES = [
  "consumption",
  "consumption_2",
  "consumption_3",
  "consumption_rest",
] as const;

export type SplitPartKind = "consumer" | "units" | "rest";

export interface SplitPart {
  /** `subjectKey` des Verbrauchers, sonst die Größe (`unit_consumption`, `residual_consumption`). */
  readonly key: string;
  readonly kind: SplitPartKind;
  readonly label: string;
  /** Die Farbstufe als Größenschlüssel — `null`, wenn nicht gestapelt wird. */
  readonly shade: string | null;
  readonly total: Total | undefined;
  /** Anteil am Verbrauch des Zeitraums, 0…1 — `null`, wenn eine der Summen fehlt. */
  readonly share: number | null;
  readonly series: EnergySeries | undefined;
  /**
   * Woher dieser Teil kam — PV-Anlage oder Netz (HM-120). Rechnet die API, über
   * dieselben Viertelstunden für beide Anteile; fehlt, wo das Konzept keinen
   * PV-Anteil kennt.
   */
  readonly origin: Origin | undefined;
  /** Bei den Wohneinheiten: wie viele es sind. */
  readonly count: number | null;
}

export interface ConsumptionSplit {
  readonly kind: "house" | "tenant" | "unit";
  readonly consumption: Total;
  /** In der Reihenfolge des Stapels, von unten nach oben. */
  readonly parts: readonly SplitPart[];
  readonly consumerCount: number;
  /** Nur mit gerechnetem Rest: welcher Anteil gemessen ist — Verbrauch minus Rest, geteilt durch Verbrauch. */
  readonly measuredShare: number | null;
  /** Ob sich Diagramm und Balken als Stapel zeichnen lassen. */
  readonly stackable: boolean;
}

export function consumptionSplit(energy: Energy): ConsumptionSplit | null {
  const totalOf = (key: string) => energy.totals.find((t) => t.quantityKey === key);
  const seriesOf = (key: string) => energy.series.find((s) => s.quantityKey === key);
  const consumption = totalOf("consumption");
  const restTotal = totalOf("residual_consumption");
  const unitsTotal = totalOf("unit_consumption");
  /* Verbraucher hinter einer Wohnung stecken in ihr; eigene Teile sind nur die der Anlage.
     **Auch die „hinter dem Eigen-Zähler"** (`position: behind_own_unit`, ohne
     Wohneinheit) sind ein eigener Teil und keine Doppelzählung (HM-190 geprüft):
     Der Verbrauch der selbst genutzten Wohnung ist im Graphen die Eigenschiene
     **ohne** sie (`max(Eigenschiene − Σ Verbraucher dahinter, 0)`), der
     Hausverbrauch die Eigenschiene **mit** ihnen. Sie fehlen also in
     `unit_consumption` und stehen hier daneben. */
  const subjects = energy.subjects.filter((s) => s.kind === "consumer" && s.siteAreaId === null);
  if (consumption === undefined || subjects.length === 0) return null;
  const kind = restTotal !== undefined ? "house" : unitsTotal !== undefined ? "tenant" : null;
  if (kind === null) return null;

  const shareOf = (total: Total | undefined): number | null =>
    total?.value == null || consumption.value === null || consumption.value <= 0
      ? null
      : total.value / consumption.value;

  const consumers = subjects.map((subject): Omit<SplitPart, "shade"> => {
    const total = subject.totals.find((t) => t.quantityKey === "load_consumption");
    return {
      key: subject.subjectKey,
      kind: "consumer",
      label: subject.label,
      total,
      share: shareOf(total),
      series: subject.series.find((entry) => entry.quantityKey === "load_consumption"),
      origin: total?.origin,
      count: null,
    };
  });
  const aggregate = (
    key: string,
    partKind: SplitPartKind,
    total: Total,
  ): Omit<SplitPart, "shade"> => ({
    key,
    kind: partKind,
    label: kind === "tenant" ? "Wohneinheiten" : total.label,
    total,
    share: shareOf(total),
    series: seriesOf(key),
    origin: total.origin,
    count: partKind === "units" ? energy.subjects.filter((s) => s.kind === "unit").length : null,
  });

  /* Im Haus steht der gerechnete Rest oben und bekommt die blasseste Stufe;
     im Mieterstrom stehen die Wohnungen unten und bekommen die kräftigste. */
  const ordered =
    kind === "house"
      ? [...consumers, aggregate("residual_consumption", "rest", restTotal!)]
      : [aggregate("unit_consumption", "units", unitsTotal!), ...consumers];

  const stackable =
    ordered.length <= CONSUMPTION_SHADES.length &&
    ordered.every((part) => part.series !== undefined);
  const shadeOf = (index: number): string | null => {
    if (!stackable) return null;
    /* Der Rest ist immer die blasseste Stufe, auch bei einem einzigen Verbraucher. */
    return kind === "house" && index === ordered.length - 1
      ? "consumption_rest"
      : CONSUMPTION_SHADES[index]!;
  };

  const restShare = kind === "house" ? shareOf(restTotal) : null;
  return {
    kind,
    consumption,
    parts: ordered.map((part, index) => ({ ...part, shade: shadeOf(index) })),
    consumerCount: consumers.length,
    measuredShare: restShare === null ? null : Math.min(1, Math.max(0, 1 - restShare)),
    stackable,
  };
}

/**
 * Die Verbraucher hinter dem Zähler einer Wohnung und ihr übriger Verbrauch
 * (HM-111, HM-118). `null` ohne Verbraucher — dann gibt es nichts aufzuteilen.
 */
export function unitConsumptionSplit(energy: UnitEnergy): ConsumptionSplit | null {
  const consumption = energy.totals.find((t) => t.quantityKey === "unit_consumption");
  const restTotal = energy.totals.find((t) => t.quantityKey === "residual_consumption");
  if (consumption === undefined || restTotal === undefined || energy.consumers.length === 0) {
    return null;
  }
  const shareOf = (total: Total | undefined): number | null =>
    total?.value == null || consumption.value === null || consumption.value <= 0
      ? null
      : total.value / consumption.value;

  const ordered: Omit<SplitPart, "shade">[] = [
    ...energy.consumers.map((consumer): Omit<SplitPart, "shade"> => {
      const total = consumer.totals.find((t) => t.quantityKey === "load_consumption");
      return {
        key: consumer.subjectKey,
        kind: "consumer",
        label: consumer.label,
        total,
        share: shareOf(total),
        series: consumer.series.find((entry) => entry.quantityKey === "load_consumption"),
        origin: total?.origin,
        count: null,
      };
    }),
    {
      key: "residual_consumption",
      kind: "rest",
      /* Die Größe trägt den Namen der Wohnung, und auf ihrer Seite steht er schon oben. */
      label: "Übriger Verbrauch",
      total: restTotal,
      share: shareOf(restTotal),
      series: energy.series.find((s) => s.quantityKey === "residual_consumption"),
      origin: restTotal.origin,
      count: null,
    },
  ];
  const stackable =
    ordered.length <= CONSUMPTION_SHADES.length &&
    ordered.every((part) => part.series !== undefined);
  const restShare = shareOf(restTotal);
  return {
    kind: "unit",
    consumption,
    parts: ordered.map((part, index) => ({
      ...part,
      shade: !stackable
        ? null
        : index === ordered.length - 1
          ? "consumption_rest"
          : CONSUMPTION_SHADES[index]!,
    })),
    consumerCount: energy.consumers.length,
    measuredShare: restShare === null ? null : Math.min(1, Math.max(0, 1 - restShare)),
    stackable,
  };
}

/**
 * PV- und Netzanteil als Bruch (HM-120) — Anteile **an der Summe der beiden**,
 * nicht am Verbrauch daneben: Die Herkunft kann über weniger Viertelstunden
 * gebildet sein als der Verbrauch, und 40 % PV von einem größeren Ganzen wäre
 * eine kleinere Zahl, als gemessen wurde. `null`, wenn sich nichts teilen lässt.
 */
export function originRatios(
  origin: Origin | undefined,
): { readonly pv: number; readonly grid: number } | null {
  if (origin?.pv == null || origin.grid == null) return null;
  const whole = origin.pv + origin.grid;
  if (whole <= 0) return null;
  return { pv: origin.pv / whole, grid: origin.grid / whole };
}

/** Ein Streifen im Stapel „Verbrauch" des Mieterstroms. */
export interface StackBand {
  readonly key: string;
  readonly label: string;
  /** Stufe innerhalb der Größe Verbrauch, 0 (kräftig) bis `MAX_FADE` (blass). */
  readonly fade: number;
  readonly series: EnergySeries;
}

/**
 * Wie weit der blasseste Streifen zum Grund hin gemischt ist. Weiter nicht:
 * Darüber verschwimmt er im dunklen Thema mit der Karte.
 */
export const MAX_FADE = 0.7;

/**
 * **Der Verbrauch im Mieterstrom je Wohnung und je Verbraucher** (HM-118): unten
 * die Wohnungen, darüber die weiteren Verbraucher der Anlage, als
 * Helligkeitsreihe derselben Größe vom kräftigsten zum blassesten. Anders als
 * in `consumptionSplit` gibt es hier **keine Grenze**: Ab etwa sechs Streifen
 * sind benachbarte Stufen im Bild kaum zu unterscheiden, gelesen wird dann über
 * Ablesefenster und Legende — so entschieden, statt jeder Wohnung eine eigene
 * Farbe zu geben (ADR 0013, OQ-032). Die Fugen zwischen den Segmenten zeichnet
 * das Diagramm.
 *
 * `null`, wenn es kein Mieterstrom ist oder einer Wohnung bzw. einem
 * Verbraucher die Reihe fehlt — ein Stapel mit fehlendem Teil wäre niedriger
 * als der Verbrauch und sähe trotzdem vollständig aus.
 */
export function tenantConsumptionStack(energy: Energy): readonly StackBand[] | null {
  if (!energy.totals.some((t) => t.quantityKey === "unit_consumption")) return null;
  const units = energy.subjects.filter((s) => s.kind === "unit");
  const consumers = energy.subjects.filter((s) => s.kind === "consumer" && s.siteAreaId === null);
  if (units.length === 0) return null;

  const members = [
    ...units.map((s) => ({ subject: s, quantity: "unit_consumption" })),
    ...consumers.map((s) => ({ subject: s, quantity: "load_consumption" })),
  ];
  const bands = members.map(({ subject, quantity }) => ({
    subject,
    series: subject.series.find((entry) => entry.quantityKey === quantity),
  }));
  if (bands.some((band) => band.series === undefined)) return null;

  const last = Math.max(1, bands.length - 1);
  return bands.map(({ subject, series }, index) => ({
    key: subject.subjectKey,
    label: subject.label,
    fade: bands.length === 1 ? 0 : (index / last) * MAX_FADE,
    series: series!,
  }));
}
